<template>
  <div v-if="!hidden" class="sp-rating" :class="layoutModifiers" part="rating" :style="rootStyle">
    <component
      :is="linkTag"
      v-if="showLeadingScore"
      v-bind="linkAttributes"
      class="sp-rating__score"
      part="score"
      v-on="linkListeners"
    >
      {{ score }}
    </component>

    <span v-if="showLeadingCount" class="sp-rating__count" part="count">
      <strong>{{ ratingCount }}</strong> {{ t("rating_count_label", ratingCount) }}
    </span>

    <component :is="starsTag" v-bind="starsAttributes" class="sp-rating__stars" part="stars" v-on="linkListeners">
      <span v-for="(name, index) in stars" :key="index" class="sp-rating__star" aria-hidden="true">
        <sp-icon class="sp-rating__icon" :name="name"></sp-icon>
      </span>
    </component>

    <component
      :is="linkTag"
      v-if="showTrailingScore"
      v-bind="linkAttributes"
      class="sp-rating__score"
      part="score"
      v-on="linkListeners"
    >
      {{ score }}
    </component>

    <a
      v-if="infoUrl"
      class="sp-rating__info-icon"
      part="info-icon"
      :href="infoHref"
      v-bind="infoTargetAttributes"
      :aria-label="tAccessibleName('rating_info')"
      v-on="infoListeners"
    >
      <sp-icon :name="infoIcon" aria-hidden="true"></sp-icon>
    </a>

    <a
      v-if="showInfoText"
      class="sp-rating__info"
      part="info"
      :href="infoHref"
      v-bind="infoTargetAttributes"
      v-on="infoListeners"
    >
      {{ infoText }}
    </a>

    <span v-if="showTrailingCount" class="sp-rating__count" part="count">
      <strong>{{ ratingCount }}</strong> {{ t("rating_count_label", ratingCount) }}
    </span>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { t, tAccessibleName } from "#ce/shared/components-i18n";
import { toBoolean } from "#ce/utils/props";
import { RatingLayout, props as ratingProps } from "./props.js";
import { MAX_RATING, useStars } from "./composables/useStars.js";

const props = defineProps(ratingProps);

const noScore = computed(() => toBoolean(props.noScore));
const ratingCount = computed(() => Number(props.count) || 0);

// The largest digit count `Number#toFixed` accepts.
const MAX_FRACTION_DIGITS = 100;

const rating = computed(() => {
  const value = Number(props.rating);

  return Number.isFinite(value) && value > 0 ? Math.min(value, MAX_RATING) : 0;
});

const STAR_ICONS = Object.freeze({
  full: "rating-star-full-color",
  half: "rating-star-half-color",
  empty: "rating-star-empty-color",
});

const glyphs = useStars(rating, { empty: () => toBoolean(props.emptystars) });
const stars = computed(() => glyphs.value.map((glyph) => STAR_ICONS[glyph]));

const summary = computed(() => props.layout === RatingLayout.SUMMARY);
const layoutModifiers = computed(() => {
  if (!summary.value) {
    return {};
  }

  return { "--layout-summary": true, [`--summary-from-${props.summaryBreakpoint}`]: true };
});

const fractionDigits = computed(() => {
  const digits = Number(props.fractionDigits);

  const supported = Number.isInteger(digits) && digits >= 0 && digits <= MAX_FRACTION_DIGITS;

  return props.fractionDigits !== undefined && supported ? digits : undefined;
});

function formatScore(value) {
  return fractionDigits.value === undefined ? value : value.toFixed(fractionDigits.value);
}

const hidden = computed(() => toBoolean(props.hideunrated) && !rating.value);
const showCount = computed(() => toBoolean(props.showcount) && Boolean(rating.value) && ratingCount.value > 0);
const showInfoText = computed(() => Boolean(props.infoUrl && props.infoText));

// The summary layout reads score, count, stars, info, the order its grid shows them in.
const showLeadingScore = computed(() => summary.value && !noScore.value);
const showTrailingScore = computed(() => !summary.value && !noScore.value);
const showLeadingCount = computed(() => summary.value && showCount.value);
const showTrailingCount = computed(() => !summary.value && showCount.value);

// An empty no-rating wording falls through to zero.
const score = computed(() =>
  rating.value ? formatScore(rating.value) : (props.noRating ?? t("rating_not_rated")) || 0,
);

const starsLabel = computed(() => {
  if (!rating.value) {
    return score.value;
  }

  return props.label || tAccessibleName("rating_stars_label", { score: rating.value, max: MAX_RATING });
});

// A bare fragment resolves against the page, so a `<base href>` on the page cannot send it elsewhere.
function pageHref(url) {
  return url?.startsWith("#") ? `${window.location.href.split("#")[0]}${url}` : url;
}

// A bare fragment link points at the page as it is when used, so a query the page set after render stays. Press and
// context menu cover a new tab or window, which read the href before any click. Listeners can still cancel the click.
function refreshFragmentHref(event, url) {
  if (url?.startsWith("#")) {
    event.currentTarget.href = pageHref(url);
  }
}

function refreshLinkHref(event) {
  refreshFragmentHref(event, props.url);
}

function refreshInfoHref(event) {
  refreshFragmentHref(event, props.infoUrl);
}

const linkListeners = { click: refreshLinkHref, pointerdown: refreshLinkHref, contextmenu: refreshLinkHref };
const infoListeners = { click: refreshInfoHref, pointerdown: refreshInfoHref, contextmenu: refreshInfoHref };

const href = computed(() => pageHref(props.url));
const infoHref = computed(() => pageHref(props.infoUrl));

// The summary numbers its columns over the parts it renders, so a missing part leaves no empty track.
const summaryColumns = computed(() => {
  let column = showLeadingScore.value ? 2 : 1;
  const columns = {};

  if (stars.value.length || showLeadingCount.value) {
    columns["--rating-summary-stack-column"] = column++;
  }

  columns["--rating-summary-info-icon-column"] = column++;
  columns["--rating-summary-info-column"] = column;

  return columns;
});

const rootStyle = computed(() => (summary.value ? summaryColumns.value : {}));

const linkTag = computed(() => (props.url ? "a" : "span"));

const SAME_CONTEXT_TARGETS = ["_self", "_top", "_parent"];

function linkTargetAttributes(target) {
  if (!target) {
    return {};
  }

  if (SAME_CONTEXT_TARGETS.includes(target)) {
    return { target };
  }

  const rel = [...new Set(["noopener", ...(props.rel?.split(/\s+/) ?? [])])].join(" ");

  return { target, rel };
}

const targetAttributes = computed(() => linkTargetAttributes(props.target));
const infoTargetAttributes = computed(() => linkTargetAttributes(props.infoTarget ?? props.target));

const linkAttributes = computed(() => (props.url ? { href: href.value, ...targetAttributes.value } : {}));

// An empty link is a zero-size tab stop, so the stars link only while there are stars to click.
const starsTag = computed(() => (stars.value.length ? linkTag.value : "span"));

// A label on a plain `<span>` is ignored without a role, so drawn stars that link nowhere are one image.
const starsAttributes = computed(() => {
  const attributes = { "aria-label": starsLabel.value };

  if (!stars.value.length) {
    return attributes;
  }

  if (starsTag.value === "span") {
    attributes.role = "img";
  }

  return { ...attributes, ...linkAttributes.value };
});
</script>

<style>
:host {
  --rating-icon-size: var(--sp-comp-rating-icon-size, var(--sp-sys-icon-size-xxx-small));
  --rating-star-image-size: var(--sp-comp-rating-star-image-size, 100%);
  --rating-star-spacing: var(--sp-comp-rating-star-spacing, 1px);
  --rating-gap: var(--sp-comp-rating-gap, 0);
  --rating-justify-content: var(--sp-comp-rating-justify-content, center);
  --rating-justify-content-sm: var(--sp-comp-rating-justify-content-sm, var(--rating-justify-content));
  --rating-info-icon-size: var(--sp-comp-rating-info-icon-size, var(--sp-sys-icon-size-xxx-small));
  --rating-info-icon-color: var(--sp-comp-rating-info-icon-color, var(--sp-sys-color-primary));
  --rating-font-size: var(--sp-comp-rating-font-size, var(--sp-sys-typescale-body-small-font-size));
  --rating-font-size-sm: var(--sp-comp-rating-font-size-sm, var(--sp-ref-font-size-semi-medium));
  --rating-line-height: var(--sp-comp-rating-line-height, var(--sp-sys-typescale-body-small-line-height));
  --rating-vertical-spacing: var(--sp-comp-rating-vertical-spacing, var(--sp-ref-scale-8));
  --rating-horizontal-spacing: var(--sp-comp-rating-horizontal-spacing, var(--sp-ref-scale-4));
  --rating-horizontal-spacing-end: var(--sp-comp-rating-horizontal-spacing-end, var(--rating-horizontal-spacing));
  --rating-font-family: var(--sp-comp-rating-font-family, inherit);
  --rating-font-weight: var(--sp-comp-rating-font-weight, inherit);
  --rating-score-color: var(--sp-comp-rating-score-color, inherit);
  --rating-count-font-size: var(--sp-comp-rating-count-font-size, var(--sp-ref-font-size-semi-medium));
  --rating-count-font-weight: var(--sp-comp-rating-count-font-weight, var(--sp-sys-font-weight-bold));
  --rating-count-line-height: var(--sp-comp-rating-count-line-height, inherit);
  --rating-count-label-font-weight: var(--sp-comp-rating-count-label-font-weight, inherit);
  --rating-count-spacing: var(--sp-comp-rating-count-spacing, 0);
  --rating-info-font-size: var(--sp-comp-rating-info-font-size, var(--sp-sys-typescale-footnote-font-size));
  --rating-info-color: var(--sp-comp-rating-info-color, var(--sp-sys-color-link));
  --rating-info-text-transform: var(--sp-comp-rating-info-text-transform, uppercase);
  --rating-info-display: var(--sp-comp-rating-info-display, inline);

  display: block;
}
</style>

<style scoped lang="scss">
@mixin summary-columns($from) {
  .sp-rating.--summary-from-#{$from} {
    display: grid;
    grid-auto-columns: auto;
    column-gap: var(--rating-gap);
    row-gap: 0;
  }

  .sp-rating.--summary-from-#{$from} .sp-rating__score {
    grid-column: 1;
    grid-row: 1 / 3;
  }

  .sp-rating.--summary-from-#{$from} .sp-rating__count {
    grid-column: var(--rating-summary-stack-column);
    grid-row: 1;
    text-align: start;
  }

  .sp-rating.--summary-from-#{$from} .sp-rating__stars {
    grid-column: var(--rating-summary-stack-column);
  }

  .sp-rating.--summary-from-#{$from} .sp-rating__info-icon {
    grid-column: var(--rating-summary-info-icon-column);
    grid-row: 1 / 3;
  }

  .sp-rating.--summary-from-#{$from} .sp-rating__info {
    grid-column: var(--rating-summary-info-column);
    grid-row: 1 / 3;
  }
}

.sp-rating {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: var(--rating-justify-content);
  gap: var(--rating-gap);
  margin: var(--rating-vertical-spacing) 0;
}

a {
  text-decoration: none;
}

.sp-rating__stars {
  display: flex;
}

.sp-rating__star {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: var(--rating-icon-size);
  height: var(--rating-icon-size);
  min-width: var(--rating-icon-size);
  min-height: var(--rating-icon-size);
  margin: 0 var(--rating-star-spacing);
}

.sp-rating__icon {
  --sp-comp-icon-size: 100%;

  flex: none;
  width: var(--rating-star-image-size);
  height: var(--rating-star-image-size);
  font-size: inherit;
}

.sp-rating__count {
  flex-basis: 100%;
  font-size: var(--rating-count-font-size);
  font-weight: var(--rating-count-label-font-weight);
  line-height: var(--rating-count-line-height);
  text-align: center;
}

.sp-rating__count strong {
  font-weight: var(--rating-count-font-weight);
}

.sp-rating__score {
  color: var(--rating-score-color);
  font-family: var(--rating-font-family);
  font-weight: var(--rating-font-weight);
  margin-block: 0;
  margin-inline: var(--rating-horizontal-spacing) var(--rating-horizontal-spacing-end);
  font-size: var(--rating-font-size);
  line-height: var(--rating-line-height);
  white-space: nowrap;
}

.sp-rating.--layout-summary .sp-rating__count {
  margin-block-end: var(--rating-count-spacing);
}

/* $breakpoint-sm from assets/stylesheets/mixins/_breakpoint.scss. */
@media (min-width: 751px) {
  .sp-rating {
    justify-content: var(--rating-justify-content-sm);
  }

  .sp-rating__score {
    font-size: var(--rating-font-size-sm);
  }

  @include summary-columns(sm);
}

/* $breakpoint-md from assets/stylesheets/mixins/_breakpoint.scss. */
@media (min-width: 992px) {
  @include summary-columns(md);
}

.sp-rating__info-icon {
  line-height: 0;
  width: var(--rating-info-icon-size);
  height: var(--rating-info-icon-size);
  min-width: var(--rating-info-icon-size);
  min-height: var(--rating-info-icon-size);
  vertical-align: middle;
}

.sp-rating__info {
  display: var(--rating-info-display);
  color: var(--rating-info-color);
  font-size: var(--rating-info-font-size);
  text-transform: var(--rating-info-text-transform);
  margin: var(--rating-vertical-spacing) 0;
  text-align: center;
}

.sp-rating__info-icon sp-icon {
  --sp-comp-icon-size: var(--rating-info-icon-size);
  --sp-comp-icon-fill: var(--rating-info-icon-color);

  display: block;
}
</style>
